import { useState, type FormEvent } from "react";

interface RecoveryFormProps {
  loading: boolean;
  error: string;
  onSubmit: (email: string) => void;
  onBack: () => void;
}

const baseField =
  "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100";

export default function RecoveryForm({ loading, error, onSubmit, onBack }: RecoveryFormProps) {
  const [fieldError, setFieldError] = useState("");

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = String(new FormData(event.currentTarget).get("email") ?? "").trim();
    if (!email) {
      setFieldError("Ingresa tu correo.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFieldError("Correo no válido.");
      return;
    }
    setFieldError("");
    onSubmit(email);
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
      <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
        <i className="ri-lock-password-line text-xl leading-none" aria-hidden="true"></i>
      </span>
      <h1 className="mt-4 text-xl font-heading font-bold text-foreground-950">Recuperar contraseña</h1>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        Ingresa tu correo y te enviaremos un código para restablecer tu contraseña.
      </p>

      <form className="mt-6 space-y-4" noValidate onSubmit={handleSubmit}>
        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-label font-medium text-foreground-700">
            Correo electrónico
          </label>
          <input id="email" name="email" type="email" autoComplete="email" placeholder="admin@axoniagroup.com" className={baseField} />
          {fieldError && <p className="mt-1 text-xs text-primary-700">{fieldError}</p>}
        </div>

        {error && (
          <p role="alert" className="flex items-start gap-2 rounded-md bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
            <i className="ri-error-warning-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-base font-label font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-foreground-950"
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base leading-none" aria-hidden="true"></i>
              Enviando…
            </>
          ) : (
            "Enviar código"
          )}
        </button>
      </form>

      <button
        type="button"
        onClick={onBack}
        className="mt-5 flex cursor-pointer items-center gap-1 text-sm font-label font-medium text-foreground-500 transition-colors hover:text-foreground-800"
      >
        <i className="ri-arrow-left-line text-base leading-none" aria-hidden="true"></i>
        Volver al inicio de sesión
      </button>
    </div>
  );
}